// ============================================================================
// CRAFTINGMENU.JS — Bottom-sheet crafting UI for Palm Island (pi-tx9.3)
// ============================================================================
import { getAvailableRecipes, getAffordableRecipes, canAffordRecipe, RECIPE_REGISTRY } from '../systems/crafting.js';
import { getInventory, RESOURCE_DEFS } from '../systems/resources.js';
import { getDiscoveryCount } from './discovery.js';
import { eventBus, GameEvents } from '../systems/events.js';

let hammerButton = null;
let sheetOverlay = null;
let sheetContainer = null;
let isOpen = false;

// Drag-to-dismiss state
let dragStartY = 0;
let dragCurrentY = 0;
let isDragging = false;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
function injectStyles() {
  const style = document.createElement('style');
  style.textContent = `
    /* ---- Hammer button (bottom-right, parchment style) ---- */
    #crafting-btn {
      position: fixed;
      bottom: 16px;
      right: 16px;
      z-index: 900;
      background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
      border: 2px solid #c4a265;
      border-radius: 12px;
      padding: 8px 14px;
      font-family: 'Georgia', serif;
      font-size: 15px;
      color: #5a3e1b;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
      display: none;
      align-items: center;
      gap: 6px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    }
    #crafting-btn.crafting-btn-visible {
      display: flex;
    }
    #crafting-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
    }
    #crafting-btn:active {
      transform: scale(0.97);
    }
    #crafting-badge {
      background: #a0853a;
      color: #fff;
      border-radius: 10px;
      padding: 1px 6px;
      font-size: 12px;
      font-weight: bold;
      min-width: 16px;
      text-align: center;
      line-height: 1.4;
    }

    /* ---- Bottom-sheet overlay ---- */
    #crafting-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 940;
      background: rgba(0, 0, 0, 0.35);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }
    #crafting-overlay.crafting-open {
      opacity: 1;
      pointer-events: auto;
    }

    /* ---- Bottom sheet ---- */
    #crafting-sheet {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      max-height: 40vh;
      z-index: 950;
      background: linear-gradient(145deg, #faf0d7 0%, #f0deb0 50%, #e8d5a3 100%);
      border-top: 3px solid #c4a265;
      border-radius: 20px 20px 0 0;
      box-shadow: 0 -6px 30px rgba(0, 0, 0, 0.3);
      font-family: 'Georgia', serif;
      color: #5a3e1b;
      transform: translateY(100%);
      transition: transform 0.3s ease;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    #crafting-sheet.crafting-open {
      transform: translateY(0);
    }

    /* ---- Drag handle ---- */
    .crafting-drag-handle {
      display: flex;
      justify-content: center;
      padding: 10px 0 6px;
      cursor: grab;
      flex-shrink: 0;
    }
    .crafting-drag-handle:active {
      cursor: grabbing;
    }
    .crafting-drag-bar {
      width: 40px;
      height: 5px;
      background: #c4a265;
      border-radius: 3px;
      opacity: 0.6;
    }

    /* ---- Sheet header ---- */
    .crafting-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 20px 10px;
      border-bottom: 2px solid #c4a265;
      flex-shrink: 0;
    }
    .crafting-title {
      font-size: 20px;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .crafting-close {
      background: none;
      border: 2px solid #c4a265;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      font-size: 18px;
      color: #5a3e1b;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
      font-family: 'Georgia', serif;
    }
    .crafting-close:hover {
      background: #c4a265;
      color: #fff;
    }

    /* ---- Recipe grid ---- */
    .crafting-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      padding: 14px 20px 20px;
      overflow-y: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .crafting-grid::-webkit-scrollbar {
      display: none;
    }
    @media (max-width: 480px) {
      .crafting-grid {
        grid-template-columns: repeat(2, 1fr);
        padding: 10px 14px 16px;
      }
    }

    /* ---- Recipe card ---- */
    .crafting-card {
      background: linear-gradient(135deg, #fff8e7 0%, #f5e6c8 100%);
      border: 2px solid #d4b87a;
      border-radius: 12px;
      padding: 12px 8px 10px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    .crafting-card.affordable {
      border-color: #a0853a;
      cursor: pointer;
    }
    .crafting-card.affordable:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(160, 133, 58, 0.3);
    }
    .crafting-card.affordable:active {
      transform: translateY(0);
    }
    .crafting-card.unaffordable {
      opacity: 0.5;
      border-color: #d4b87a;
    }
    .crafting-card.locked {
      opacity: 0.3;
      border-color: #d4b87a;
    }

    .crafting-card-icon {
      font-size: 30px;
      line-height: 1;
    }
    .crafting-card-name {
      font-size: 13px;
      font-weight: bold;
      line-height: 1.2;
    }

    /* ---- Cost list ---- */
    .crafting-cost-list {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 4px;
      margin-top: 2px;
    }
    .crafting-cost-item {
      font-size: 11px;
      white-space: nowrap;
    }
    .crafting-cost-item.has-enough {
      color: #3a7a3a;
    }
    .crafting-cost-item.not-enough {
      color: #a03a3a;
    }

    .crafting-card-hint {
      font-size: 10px;
      font-style: italic;
      color: #a08a6a;
      margin-top: 2px;
      line-height: 1.2;
    }

    .crafting-locked-icon {
      font-size: 24px;
      opacity: 0.5;
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Hammer button
// ---------------------------------------------------------------------------
function createHammerButton() {
  hammerButton = document.createElement('button');
  hammerButton.id = 'crafting-btn';
  hammerButton.addEventListener('click', () => {
    if (isOpen) {
      closeSheet();
    } else {
      openSheet();
    }
  });
  document.body.appendChild(hammerButton);
  updateHammerBadge();
}

function updateHammerBadge() {
  if (!hammerButton) return;

  const { discovered } = getDiscoveryCount();
  if (discovered < 5) {
    hammerButton.classList.remove('crafting-btn-visible');
    return;
  }

  hammerButton.classList.add('crafting-btn-visible');

  const affordable = getAffordableRecipes();
  const count = affordable.length;

  if (count > 0) {
    hammerButton.innerHTML = `\uD83D\uDD28 <span id="crafting-badge">${count}</span>`;
  } else {
    hammerButton.innerHTML = `\uD83D\uDD28`;
  }
}

// ---------------------------------------------------------------------------
// Bottom sheet
// ---------------------------------------------------------------------------
function createSheet() {
  // Semi-transparent overlay (click to close)
  sheetOverlay = document.createElement('div');
  sheetOverlay.id = 'crafting-overlay';
  sheetOverlay.addEventListener('click', closeSheet);
  document.body.appendChild(sheetOverlay);

  // Sheet container
  sheetContainer = document.createElement('div');
  sheetContainer.id = 'crafting-sheet';
  document.body.appendChild(sheetContainer);
}

// ---------------------------------------------------------------------------
// Render recipe cards
// ---------------------------------------------------------------------------
function renderRecipeCards() {
  if (!sheetContainer) return;

  const allRecipes = Object.values(RECIPE_REGISTRY);
  const { discovered } = getDiscoveryCount();
  const inventory = getInventory();

  let html = '';

  // Drag handle
  html += `
    <div class="crafting-drag-handle" id="crafting-drag-handle">
      <div class="crafting-drag-bar"></div>
    </div>
  `;

  // Header
  html += `
    <div class="crafting-header">
      <div class="crafting-title">\uD83D\uDD28 Crafting</div>
      <button class="crafting-close" aria-label="Close">\u00D7</button>
    </div>
  `;

  // Recipe grid
  html += '<div class="crafting-grid">';

  for (const recipe of allRecipes) {
    const isUnlocked = discovered >= recipe.discoveryRequirement;
    const isAffordable = isUnlocked && canAffordRecipe(recipe.id);

    if (!isUnlocked) {
      // Locked card
      html += `
        <div class="crafting-card locked">
          <div class="crafting-locked-icon">\uD83D\uDD12</div>
          <div class="crafting-card-name">???</div>
          <div class="crafting-card-hint">${recipe.discoveryRequirement} discoveries</div>
        </div>
      `;
    } else if (isAffordable) {
      // Affordable card
      html += `
        <div class="crafting-card affordable" data-recipe-id="${recipe.id}">
          <div class="crafting-card-icon">${recipe.icon}</div>
          <div class="crafting-card-name">${recipe.name}</div>
          <div class="crafting-cost-list">${renderCostList(recipe.cost, inventory)}</div>
        </div>
      `;
    } else {
      // Unaffordable card
      const hint = getShortageHint(recipe.cost, inventory);
      html += `
        <div class="crafting-card unaffordable">
          <div class="crafting-card-icon">${recipe.icon}</div>
          <div class="crafting-card-name">${recipe.name}</div>
          <div class="crafting-cost-list">${renderCostList(recipe.cost, inventory)}</div>
          ${hint ? `<div class="crafting-card-hint">${hint}</div>` : ''}
        </div>
      `;
    }
  }

  html += '</div>';

  sheetContainer.innerHTML = html;

  // Attach event listeners
  const closeBtn = sheetContainer.querySelector('.crafting-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeSheet);
  }

  // Click handlers for affordable cards
  const affordableCards = sheetContainer.querySelectorAll('.crafting-card.affordable');
  affordableCards.forEach((card) => {
    card.addEventListener('click', () => {
      const recipeId = card.dataset.recipeId;
      if (recipeId) selectRecipe(recipeId);
    });
  });

  // Drag-to-dismiss on the handle
  setupDragToDismiss();
}

/**
 * Render the cost list for a recipe, colouring each item green/red.
 */
function renderCostList(cost, inventory) {
  return Object.entries(cost)
    .map(([type, needed]) => {
      const have = inventory[type] || 0;
      const def = RESOURCE_DEFS[type];
      const icon = def ? def.icon : type;
      const cls = have >= needed ? 'has-enough' : 'not-enough';
      return `<span class="crafting-cost-item ${cls}">${icon}${needed}</span>`;
    })
    .join('');
}

/**
 * Generate a short hint about what the player is missing.
 */
function getShortageHint(cost, inventory) {
  const shortages = [];
  for (const [type, needed] of Object.entries(cost)) {
    const have = inventory[type] || 0;
    if (have < needed) {
      const def = RESOURCE_DEFS[type];
      const name = def ? def.name : type;
      shortages.push(`${needed - have} more ${name}`);
    }
  }
  if (shortages.length === 0) return '';
  return `Need ${shortages[0]}`;
}

// ---------------------------------------------------------------------------
// Drag-to-dismiss
// ---------------------------------------------------------------------------
function setupDragToDismiss() {
  const handle = sheetContainer.querySelector('#crafting-drag-handle');
  if (!handle) return;

  handle.addEventListener('touchstart', onDragStart, { passive: true });
  handle.addEventListener('mousedown', onDragStart);
}

function onDragStart(e) {
  isDragging = true;
  dragStartY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
  dragCurrentY = 0;

  // Disable smooth transition during drag
  sheetContainer.style.transition = 'none';

  document.addEventListener('touchmove', onDragMove, { passive: true });
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('touchend', onDragEnd);
  document.addEventListener('mouseup', onDragEnd);
}

function onDragMove(e) {
  if (!isDragging) return;
  const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
  dragCurrentY = clientY - dragStartY;

  // Only allow dragging downward
  if (dragCurrentY < 0) dragCurrentY = 0;

  sheetContainer.style.transform = `translateY(${dragCurrentY}px)`;
}

function onDragEnd() {
  if (!isDragging) return;
  isDragging = false;

  document.removeEventListener('touchmove', onDragMove);
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('touchend', onDragEnd);
  document.removeEventListener('mouseup', onDragEnd);

  // Re-enable transition
  sheetContainer.style.transition = '';

  // If dragged more than 80px down, dismiss
  if (dragCurrentY > 80) {
    closeSheet();
  } else {
    // Snap back
    sheetContainer.style.transform = '';
  }
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------
function openSheet() {
  if (isOpen) return;
  isOpen = true;
  renderRecipeCards(); // refresh each time
  sheetOverlay.classList.add('crafting-open');
  sheetContainer.classList.add('crafting-open');
}

function closeSheet() {
  if (!isOpen) return;
  isOpen = false;
  sheetOverlay.classList.remove('crafting-open');
  sheetContainer.classList.remove('crafting-open');
  // Reset any drag offset
  sheetContainer.style.transform = '';
}

// ---------------------------------------------------------------------------
// Recipe selection
// ---------------------------------------------------------------------------
function selectRecipe(recipeId) {
  eventBus.emit(GameEvents.CRAFT_STARTED, {
    recipeId,
    recipe: RECIPE_REGISTRY[recipeId],
  });
  closeSheet();
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------
export function initCraftingMenu() {
  injectStyles();
  createHammerButton();
  createSheet();

  // Update badge when inventory changes or new discovery
  eventBus.on(GameEvents.INVENTORY_CHANGED, updateHammerBadge);
  eventBus.on(GameEvents.DISCOVERY_NEW, updateHammerBadge);

  // Escape to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeSheet();
  });

  console.log('[CraftingMenu] Initialized');
}
